import { Record, RecordKind } from './record.js';
import {
  BigNumber,
  EnumType,
  ListType,
  ObjectType,
  PrimitiveType,
  Property,
  SystemType,
  Type,
  TypeKind,
  TypeReference
} from './types.js';
import { Constraints, MinLengthConstraint, RequiredConstraint, UniqueConstraint } from './constraint.js';
import { API, APIRegistry, APIResult, IPlugin } from './plugin.js';
import { DeepModelWalker } from './internal/ModelWalker.js';
import { PropertyValue } from './internal/PropertyPathWalker.js';
import { ExternalFieldCyclicDependenciesValidator } from './validators/ExternalFieldCyclicDependenciesValidator.js';
import { ResolutionSchema } from './lookup/ResolutionSchema.js';

export type Language = string;

export interface TypesMap {
  [x: string]: Type;
}

export type SchemaReference = string;

export class SchemaTypeRef {
  schemaRef: SchemaReference;
  typeRef: TypeReference;

  constructor(schemaRef: string, typeRef: TypeReference) {
    this.schemaRef = schemaRef;
    this.typeRef = typeRef;
  }
}

export type APIImpl = { [k: string]: Function };
export type APIRegistryImpl = Map<TypeReference, APIImpl>;

export class Schema implements Record {
  _id: string | undefined;
  kind: RecordKind = RecordKind.Schema;
  types: TypesMap = {}; //Object;
  languages: Language[] = [];
  resolutionSchemas = new Map<TypeReference, ResolutionSchema>();
  public staticAPI: APIRegistryImpl = new Map<TypeReference, API>();
  public prototypeAPI: APIRegistryImpl = new Map<TypeReference, API>();

  public static string_type = new PrimitiveType('string', SystemType.String);
  public static number_type = new PrimitiveType('number', SystemType.Number);
  public static boolean_type = new PrimitiveType('boolean', SystemType.Boolean);
  public static ml_string_type = new PrimitiveType('mlstring', SystemType.MLString);
  public static big_number_type = new PrimitiveType('bignumber', SystemType.BigNumber);

  constructor(types: TypesMap = {}) {
    this
      .add(Schema.string_type)
      .add(Schema.boolean_type)
      .add(Schema.ml_string_type)
      .add(Schema.number_type)
      .add(Schema.big_number_type);

    this.addTypes(types);

    // Default API
    this.staticAPI.set('Console', {
      print: (...args: any[]) => {
        console.log('Console::print', ...args);
        return APIResult.boolean(true);
      }
    });
  }

  validate() {
    for (let type of Object.values(this.types)) {
      switch (type.kind) {
        case TypeKind.Object:
          if (type instanceof ObjectType) {
            type.properties.forEach(p => this.checkPropertyOptions(p));
            const validator = new ExternalFieldCyclicDependenciesValidator(type, this);
            validator.validate();
          } else {
            throw new Error(`Wrong type declaration for ${type.name}. Object expected`);
          }
          break;
      }
    }
  }

  listTypes(): Type[] {
    return Object.values(this.types);
  }

  addTypes(types: TypesMap): Schema {
    for (let [typeName, value] of Object.entries(types)) {
      if (typeName != value.name) {
        throw new Error(`Wrong schema configuration for type ${typeName} (name mismatch)`);
      }
      this.add(value);
    }

    return this;
  }

  add(...type: Type[]): Schema {
    type.forEach(v => {
      this.types[v.name] = v;
    });
    return this;
  }

  registerPlugin(plugin: IPlugin) {
    plugin.setup(this);
    const prototypeExtensions = plugin.registerPrototypes();
    this.extendPrototypeRegistryWithExtensions(this.prototypeAPI, prototypeExtensions);
    const staticExtensions = plugin.registerStatics();
    this.extendStaticRegistryWithExtensions(this.staticAPI, staticExtensions);
    return this;
  }

  private extendPrototypeRegistryWithExtensions(registry: APIRegistryImpl, extensions: APIRegistry) {
    extensions.forEach((value, key) => {
      this.extendPrototypeRegistryWithAPI(registry, key, value);
    });
  }

  private extendStaticRegistryWithExtensions(registry: APIRegistryImpl, extensions: APIRegistry) {
    extensions.forEach((value, key) => {
      this.extendStaticRegistryWithAPI(registry, key, value);
    });
  }

  extendPrototypeRegistryWithAPI(registry: APIRegistryImpl, typeName: string, api: API) {
    if (!registry.has(typeName)) {
      registry.set(typeName, {});
    }
    const extendedAPI = registry.get(typeName) || {};
    const self = this;
    for (let [fnName, fn] of Object.entries(api)) {
      if (extendedAPI[fnName]) {
        throw new Error(`Wrong plugin declaration: Function with ${fnName} already exists`);
      }
      extendedAPI[fnName] = function(...args: any[]) {
        const { outputType, outputValue } = fn(this, ...args);

        if (!self.getType(outputType)) {
          throw new Error(`Wrong plugin function declaration in ${fnName}: ${outputType} is not registered in schema`);
        }
        if (self.prototypeAPI.has(outputType)) {
          self.extendWithPrototype(outputValue, outputType);
        }

        return outputValue;
      };
    }
    registry.set(typeName, extendedAPI!);
  }

  extendStaticRegistryWithAPI(registry: APIRegistryImpl, typeName: string, api: API) {
    if (!registry.has(typeName)) {
      registry.set(typeName, {});
    }
    const extendedAPI = registry.get(typeName) || {};

    for (let [fnName, fn] of Object.entries(api)) {
      if (extendedAPI[fnName]) {
        throw new Error(`Function with ${fnName} already exists`);
      }
      extendedAPI[fnName] = (...args: any[]) => {
        const { outputType, outputValue } = fn(...args);

        if (!this.getType(outputType)) {
          throw new Error(`${outputType} is not registered in schema`);
        }
        if (this.prototypeAPI.has(outputType)) {
          this.extendWithPrototype(outputValue, outputType);
        }

        return outputValue;
      };
    }
    registry.set(typeName, extendedAPI!);
  }

  getType(type: TypeReference): Type | undefined {
    return this.types[type];
  }

  getConstraint(constraint: string, kwargs: any) {
    switch (constraint) {
      case Constraints.MinLength:
        return Object.assign(new MinLengthConstraint(), kwargs);
      case Constraints.Required:
        return Object.assign(new RequiredConstraint(), kwargs);
      case Constraints.UniqueItems:
        return Object.assign(new UniqueConstraint(), kwargs);

    }
  }

  getSchemaTypeReference(type: TypeReference): SchemaTypeRef {
    if (!this.types[type]) {
      throw new Error(`Type does not exist ${type}`);
    }
    return new SchemaTypeRef(this._id!, type);
  }

  private extendWithPrototypeOneLevelDeep(value: any, typeRef: TypeReference) {
    let type = this.getType(typeRef);
    if (type.kind == TypeKind.Object || type.kind == TypeKind.List) {
      if (this.prototypeAPI.has(type.name)) {
        const origin = value.__proto__;
        if (origin.extended) {
          value.__proto__ = this.prototypeAPI.get(type.name);
        } else {
          let proto: any = this.prototypeAPI.get(type.name);
          proto.extended = true;
          proto!.__proto__ = origin;
          value.__proto__ = proto;
        }
      }
    }
  }

  extendWithPrototype(value: any, typeRef: TypeReference) {
    const walker = new DeepModelWalker(this, value, typeRef);
    let propValue: PropertyValue | undefined;
    if (this.prototypeAPI.has(typeRef)) {
      this.extendWithPrototypeOneLevelDeep(value, typeRef);
    }
    while (propValue = walker.next()) {
      this.extendWithPrototypeOneLevelDeep(propValue.value, propValue.property.type);
    }
  }

  checkPropertyOptions(prop: Property) {
    const { default: def } = prop.opts;
    if (def != null) {
      const type = this.getType(prop.type);
      switch (type.kind) {
        case TypeKind.Primitive:
          if (type.getTypeReference() === Schema.big_number_type.getTypeReference()) {
            if (def instanceof BigNumber) {
              break;
            }
          }

          let expectedDefaultType = this.getExpectedPrimitiveType(type);

          if (typeof def !== expectedDefaultType) {
            throw new Error(`Wrong type definition for ${prop.name} property. Invalid type of default. ${prop.type} expected`);
          }
          break;
        case TypeKind.Enum:
          if (type instanceof EnumType) {
            // @ts-ignore
            if (!type.values.includes(def)) {
              throw new Error(`Wrong type definition for ${prop.name} property.
               Invalid type of default. ${def} should be in a list of ${type.values} values`);
            }
          } else {
            throw Error(`Wrong type definition for ${type.name}. Enum expected`);
          }
          break;
        case TypeKind.Union:
          break;
        case TypeKind.List:
          const defaultValue = prop.opts.default;
          if (!Array.isArray(defaultValue)) {
            throw new Error(`Wrong type definition for ${prop.name} property. Impossible to define default for ${prop.type} except list`);
          }
          const listType = type as ListType;
          const elementType = this.getType(listType.elementType);
          const elementExpectedType = this.getExpectedPrimitiveType(elementType);
          if (!defaultValue.every(element => typeof element === elementExpectedType)) {
            throw new Error(`Wrong type definition for ${prop.name} property. Impossible to define default for ${prop.type} except list of ${expectedDefaultType} type`);
          }
          break;
        case TypeKind.Object:
        default:
          throw new Error(`Wrong type definition for ${prop.name} property. Impossible to define default for ${prop.type}`);
      }
    }
  }

  private getExpectedPrimitiveType(type: Type) {
    let expectedDefaultType;
    if (type.getTypeReference() === Schema.string_type.getTypeReference()) {
      expectedDefaultType = 'string';
    }
    if (type.getTypeReference() === Schema.ml_string_type.getTypeReference()) {
      expectedDefaultType = 'string';
    }
    if (type.getTypeReference() === Schema.number_type.getTypeReference()) {
      expectedDefaultType = 'number';
    }

    if (type.getTypeReference() === Schema.boolean_type.getTypeReference()) {
      expectedDefaultType = 'boolean';
    }
    return expectedDefaultType;
  }

  addLanguageSupport(...langs: Language[]) {
    this.languages.push(...langs);
  }

  isLanguageRegistered(lang: Language) {
    return this.languages.indexOf(lang) > -1;
  }

  getFullObjectTypeProperties(type: ObjectType): Property[] {
    const result: Property[] = [];
    try {
      this.getFullObjectTypePropertiesRec(result, type, new Set<string>(), new Set<string>());
    } catch (e) {
      throw new Error(`Cannot find properties of type = "${type.name}". ${e.message}`);
    }
    return result;
  }

  private getFullObjectTypePropertiesRec(result: Property[],
                                         type: ObjectType,
                                         visitedProps: Set<string>,
                                         visitedComponents: Set<string>): void {
    for (const prop of type.properties) {
      if (!visitedProps.has(prop.name)) {
        visitedProps.add(prop.name);
        result.push(prop);
      }
    }
    for (const componentTypeRef of type.components) {
      const componentType = this.getType(componentTypeRef);
      if (!(componentType instanceof ObjectType)) {
        throw new Error(`Invalid component "${componentTypeRef}" kind = "${componentType.kind}". It must be kind = "Object"`);
      }
      if (visitedComponents.has(componentTypeRef)) {
        throw new Error(`Cycle detected in components hierarchy at type = "${componentType.name}" with component = "${componentTypeRef}" `);
      }
      visitedComponents.add(componentTypeRef);
      this.getFullObjectTypePropertiesRec(result, componentType, visitedProps, visitedComponents);
    }
  }
}
